/**
 * Lightweight wrapper around Web Speech Recognition.
 *
 * Listens continuously (auto-restarts on `onend`) and emits parsed keyword
 * commands. We do NOT use full transcription — we just scan for whole-word
 * matches in the latest interim transcript so the response feels instant.
 *
 * Browser support:
 *   - Chrome desktop / Android / iOS 14.5+ Safari → ✅ webkitSpeechRecognition
 *   - Firefox → ❌ (`supported` returns false)
 *
 * Voice activation is intentionally in-app only; "Hey Siri" / "OK Google"
 * require native OS shortcuts that browsers can't trigger.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const KEYWORD_RE = /\b(start|begin|go|next|done|complete|stop|cancel|pause|reset)\b/i;

function normalize(word) {
    const w = word.toLowerCase();
    if (w === 'begin' || w === 'go') return 'start';
    if (w === 'done' || w === 'complete') return 'next';
    if (w === 'cancel' || w === 'pause') return 'stop';
    return w;
}

function getRecognizer() {
    if (typeof window === 'undefined') return null;
    const Ctor =
        window.SpeechRecognition || window.webkitSpeechRecognition || null;
    if (!Ctor) return null;
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';
    return r;
}

export function useVoiceCommand({ onCommand, enabled = false } = {}) {
    const [listening, setListening] = useState(false);
    const [error, setError] = useState(null);
    const recognizerRef = useRef(null);
    const onCommandRef = useRef(onCommand);
    const lastFiredRef = useRef({ command: null, at: 0 });
    const shouldListenRef = useRef(false);

    useEffect(() => {
        onCommandRef.current = onCommand;
    }, [onCommand]);

    const supported =
        typeof window !== 'undefined' &&
        !!(window.SpeechRecognition || window.webkitSpeechRecognition);

    const stop = useCallback(() => {
        shouldListenRef.current = false;
        const r = recognizerRef.current;
        if (r) {
            try {
                r.stop();
            } catch {
                /* ignore */
            }
        }
        setListening(false);
    }, []);

    const start = useCallback(() => {
        if (!supported) {
            setError('Voice recognition is not supported in this browser.');
            return;
        }
        if (recognizerRef.current) return;

        const r = getRecognizer();
        if (!r) {
            setError('Voice recognition unavailable.');
            return;
        }
        recognizerRef.current = r;
        shouldListenRef.current = true;
        setError(null);

        r.onresult = (event) => {
            // event.results is a SpeechRecognitionResultList. We scan the LAST
            // result only — earlier ones are already finalized.
            const last = event.results[event.results.length - 1];
            if (!last) return;
            const transcript = last[0]?.transcript || '';
            const match = transcript.match(KEYWORD_RE);
            if (!match) return;
            const cmd = normalize(match[1]);
            // De-dupe: ignore the same command fired within 1.2s.
            const now = Date.now();
            if (
                lastFiredRef.current.command === cmd &&
                now - lastFiredRef.current.at < 1200
            ) {
                return;
            }
            lastFiredRef.current = { command: cmd, at: now };
            onCommandRef.current?.(cmd, transcript);
        };

        r.onerror = (e) => {
            // 'no-speech' and 'aborted' are routine — only surface fatals.
            if (e.error && e.error !== 'no-speech' && e.error !== 'aborted') {
                setError(`Voice error: ${e.error}`);
            }
        };

        r.onend = () => {
            // Auto-restart unless user explicitly stopped (mobile browsers
            // often end the session after silence).
            if (shouldListenRef.current && recognizerRef.current === r) {
                try {
                    r.start();
                } catch {
                    /* already running */
                }
            } else {
                recognizerRef.current = null;
                setListening(false);
            }
        };

        try {
            r.start();
            setListening(true);
        } catch (e) {
            setError(`Voice start failed: ${e.message}`);
            recognizerRef.current = null;
            setListening(false);
        }
    }, [supported]);

    // Auto-start/stop based on the `enabled` prop.
    useEffect(() => {
        if (enabled) start();
        else stop();
        return () => stop();
    }, [enabled, start, stop]);

    return { listening, supported, error, start, stop };
}
